///<reference path='../../WebUI/typings/tsd.d.ts'/>
import * as React from "react";
import * as ReactDOM from "react-dom";
import {BotImageControl} from './BotImageControl';
import * as Rest from '../Generated/ReactUI';

// start loading the next page of content when the bottom of gallery
// is less than PAGING_THRESHOLD away from window bottom
const PAGING_THRESHOLD = 400;

export interface SortSpec {
    defaultSortField: Rest.SortOrder,
    supportedSortFields: Rest.SortOrder[];
}

export const DEFAULT_SORT_SPEC: SortSpec = {
    defaultSortField: Rest.SortOrder.ByActivityRank,
    supportedSortFields: [Rest.SortOrder.ByActivityRank, Rest.SortOrder.ByName, Rest.SortOrder.ByDate]
};

export const FEATURED_SORT_SPEC: SortSpec = {
    defaultSortField: Rest.SortOrder.ByDisplayOrder,
    supportedSortFields: [Rest.SortOrder.ByDisplayOrder, Rest.SortOrder.ByName, Rest.SortOrder.ByDate]
};

const SORT_LABELS = ['Name', 'Date', 'Relevance', 'Feature', 'Activity'];


export interface BotGalleryControlProps extends React.Props<BotGalleryControl> {
    pageSize: number;
    sortSpec: SortSpec;
    onNextPage: () => void;
    onSort: (order: Rest.SortOrder) => void;
    defaultBotIconUrl: string;
    title?: string;
    id?: string;
    onSearch?: (s: string) => void;
    infiniteScroll?: boolean;
    baseUrl?: string;
}

export interface BotGalleryControlState {
    bots: Rest.BotDocument[];
    searchString?: string;
    loadingMore: boolean;
    sortOrder: Rest.SortOrder;
    hasMoreBots?: boolean;
    totalCount?: number;
}

export class BotGalleryControl extends React.Component<BotGalleryControlProps, BotGalleryControlState> {
    private scrollHandler: (e: JQueryEventObject) => void;
    constructor(props: BotGalleryControlProps) {
        super(props);
        this.state = {
            bots: [],
            loadingMore: true,
            hasMoreBots: true,
            sortOrder: (props.sortSpec || DEFAULT_SORT_SPEC).defaultSortField
        };
        if (this.props.onNextPage) {
            this.scrollHandler = _.debounce(_.bind(this.handleScroll, this), 100, false);
        }
    }

    public componentDidMount() {
        if (this.props.onNextPage && this.props.infiniteScroll) {
            $(window).bind('scroll', this.scrollHandler);
        }
    }

    public componentWillUnmount() {
        if (this.scrollHandler && this.props.infiniteScroll) {
            $(window).unbind('scroll', this.scrollHandler);
        }
    }

    private renderLoadingMessage(): JSX.Element {
        var pagingMessage = "Loading...";
        if (this.state.bots && this.state.bots.length) {
            const from = this.state.bots.length;
            var to = from + this.props.pageSize;
            if (_.isNumber(this.state.totalCount)) {
                to = Math.min(this.state.totalCount, to);
                pagingMessage = `Loading ${from} to ${to} of ${this.state.totalCount}...`;
            } else {
                pagingMessage = `Loading ${from} to ${to}`;
            }
        }
        return (<div className="paging-progress">{pagingMessage}</div>);
    }

    private renderLoadMoreLink(): JSX.Element {
        if (_.isNumber(this.state.totalCount)) {
            var curCount = this.state.bots ? this.state.bots.length : 0;
            var nextPageSize = this.props.pageSize;

            nextPageSize = Math.min(nextPageSize, this.state.totalCount - curCount);
            var linkText = 'Load More';
            if (nextPageSize) {
                return (
                    <div className="paging-progress">
                        <a href='' onClick={(e: React.MouseEvent<any>) => { e.preventDefault(); this.props.onNextPage(); return false; } }>{linkText}</a>
                    </div>
                );
            } else {
                return null;
            }
        }
        return this.renderLoadingMessage();
    }

    public render(): JSX.Element {
        var paging: JSX.Element = null;

        if (this.props.onNextPage) {
            if (this.state.loadingMore) {
                paging = this.renderLoadingMessage();
            } else {
                paging = this.renderLoadMoreLink();
            }
        }

        return (
            <div className="page-grid" id={this.props.id}>
                <div className="gallery-header">
                    <h2>{this.props.title}</h2>
                    {this.renderSortField() }
                </div>
                <div>
                    {this.renderSortField() }
                    {this.renderBots(this.state.bots) }
                    {paging}
                </div>
            </div>
        );
    }

    private renderSortOption(field: Rest.SortOrder) {
        if (field == this.state.sortOrder) {
            return <option value={'' + field} selected>{SORT_LABELS[field]}</option>;
        }
        return <option value={'' + field}>{SORT_LABELS[field]}</option>;
    }

    private renderSortField(): JSX.Element {
        const spec = this.props.sortSpec;
        if (!spec) {
            return null;
        }
        return (
            <div className="sort-toggle">
                <label>Sort: </label>
                <select ref="sort-select" name="sort_select" onChange={(evt: React.FormEvent<any>) => this.handleSortChange(evt) }>
                    {spec.supportedSortFields.map(f => this.renderSortOption(f)) }
                </select>
            </div>
        );
    }

    private handleSortChange(evt: React.FormEvent<any>) {
        var target = evt.target as any;
        this.props.onSort(target['value']);
    }

    private handleScroll() {
        var $gallery = $(ReactDOM.findDOMNode(this.refs['gallery']));
        var $win = $(window);
        var galleryBottom = $gallery.scrollTop() + $gallery.height();
        var windowBottom = $win.scrollTop() + $win.height();
        if (galleryBottom - windowBottom < PAGING_THRESHOLD && !this.state.loadingMore) {
            this.props.onNextPage();
        }
    }

    private renderBots(bots: Rest.BotDocument[]): JSX.Element {
        var items = bots.map(bot => this.renderBot(bot));
        return (
            <div className="inner-grid">{items}</div>
        );
    }

    private renderBot(bot: Rest.BotDocument): JSX.Element {
        var botDetailsUrl = (this.props.baseUrl || '/') + "bot?id=" + bot.Id;

        return (
            <div className="agent-card grid-card hoverable" key={bot.Id}>
                <a href={botDetailsUrl} >
                    <BotImageControl className="image-cell" bot={bot} defaultIconUrl={this.props.defaultBotIconUrl}/>
                    <div>
                        <div className="info-cell">
                            <div className="title">{bot.Name}</div>
                            <div className="publisher">{bot.Publisher}</div>
                        </div>
                        <div className="desc-cell">
                            <div className="description">{bot.Description}</div>
                        </div>
                    </div>
                </a>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./../Intercom.ReactUI/Views/BotGalleryControl.tsx